
import olView from "ol/View";
import Map from "../../MapComponents/Map/Map";
import osm from "../../MapComponents/Source/osm";
import { memo, useEffect, useMemo, useState } from "react";
import { fromLonLat } from "ol/proj";
import TileLayer from "../../MapComponents/Layers/TileLayer";
import Layers from "../../MapComponents/Layers/Layers";
import PinPointNavbar from "../../Components/PinPointNavbar/PinPointNavbar";
import { Container } from "react-bootstrap";
import { Sessions } from "../Sessions/Sessions";
import { getPingSession } from "../../Api/PingSessionApi";
import { Feature } from "ol";
import { Point } from "ol/geom";
import VectorLayer from "../../MapComponents/Layers/VectorLayer";
import MyVectorSource from "../../MapComponents/Source/VectorSource";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import Overlays from "../../MapComponents/Overlays/Overlays";
import PinPointInfoOverlay from "../../MapComponents/Overlays/PinPointInfoOverlay";
import { LoadingSpinner } from "../../Components/LoadingSpinner/LoadingSpinner";
import { SessionSummary } from "../../Components/SessionSummary";
import { usePositionGradientStyle, useSpeedGradientStyle } from "../../MapComponents/Styles/sessionPointStyles";
import { getSmoothedSpeeds } from "../../Helpers/SpeedSmoothing";

export const SessionsImproved = () => {
    const [view, setView] = useState(null);
    const [showSessionsModal, setShowSessionsModal] = useState(false);
    const [features, setFeatures] = useState([]);
    const [infoOverlayFeature, setInfoOverlayFeature] = useState(null);
    const { pingSessionId } = useParams();
    const [isViewInitialized, setIsViewInitialized] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(undefined);
    const [isLoaded, setIsLoaded] = useState(false);
    const [pingSession, setPingSession] = useState(undefined);
    const [showSpeedColors, setShowSpeedColors] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        setView(new olView({
            center: fromLonLat([-0.099912, 51.513671]),
            zoom: 16,
        }));
        setIsViewInitialized(true);
    }, []);

    useEffect(() => {
        console.log("isloaded changed", isLoaded);
    }, [isLoaded]);

    useEffect(() => {
        if (!isViewInitialized) return;
        setIsLoaded(false);
        if (pingSessionId) {
            setShowSessionsModal(false);

            const handedOffSession = location.state?.pingSession;
            if (handedOffSession && String(handedOffSession.pingSessionId) === String(pingSessionId)) {
                // Coming back from the timeline view with the session data it
                // was handed off — reuse it instead of re-fetching.
                applyPingSession(handedOffSession, true);
            } else {
                loadPingSession(pingSessionId);
            }
        } else {
            // If we're at the base route without a session ID, show the sessions modal
            setShowSessionsModal(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pingSessionId, isViewInitialized]);

    const applyPingSession = (pingSession, immediate = false) => {
        setPingSession(pingSession);
        const smoothedSpeeds = getSmoothedSpeeds(pingSession.pings);
        const builtFeatures = pingSession.pings.map((ping, index) => {
            let point = new Point(
                fromLonLat([
                    ping.position.coordinates[0],
                    ping.position.coordinates[1],
                ])
            );
            let feature = new Feature(point);
            feature.set("id", ping.pingId);
            feature.set("createDate", ping.createDate);
            feature.set("pingSessionId", ping.pingSessionId);
            feature.set("speed", ping.speedKilometresPerHour);
            feature.set("smoothedSpeed", smoothedSpeeds[index]);
            feature.set("acceleration", ping.accelerationMetresPerSecond);
            feature.set("index", index);
            feature.set("totalPings", pingSession.pings.length);
            return feature;
        });

        setFeatures(builtFeatures);

        if (builtFeatures.length > 0) {
            let minX = Infinity,
                minY = Infinity,
                maxX = -Infinity,
                maxY = -Infinity;

            for (let i = 0; i < builtFeatures.length; i++) {
                const f = builtFeatures[i];
                const cords = f.getGeometry().flatCoordinates;
                const x = cords[0];
                const y = cords[1];
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
            }

            const buffer = 2000;
            const bufferedExtent = [
                minX - buffer,
                minY - buffer,
                maxX + buffer,
                maxY + buffer,
            ];
            // Zoom to the extent
            view && view.fit(bufferedExtent, { duration: 1000 });

            if (immediate) {
                setLoadingMessage(undefined);
                setIsLoaded(true);
            } else {
                setTimeout(() => {
                    setLoadingMessage(undefined);
                    setIsLoaded(true);
                }, 650);
            }
        }
    }

    const loadPingSession = (id) => {
        setLoadingMessage("Loading session data...");
        getPingSession(id).then((pingSession) => {
            applyPingSession(pingSession);
        });
    }

    const handleSessionSelected = (pingSessionId) => {
        navigate(`/sessions/${pingSessionId}`);
    }

    const handleNextFeatureClick = (feature, direction) => {
        // find index of existing feature by Id
        const index = features.findIndex((f) => f.get("id") === feature.get("id"));

        const nextFeature = features[index + direction];
        setInfoOverlayFeature(nextFeature);

        view.setCenter(nextFeature.getGeometry().getCoordinates());
    };

    const handlePointerClick = (e) => {
        const map = e.map;
        const features = map.getFeaturesAtPixel(e.pixel, {
            hitTolerance: 4,
        });
        const pointFeatures = features.filter(
            (feature) => feature.getGeometry() instanceof Point
        );

        if (pointFeatures.length > 0) {
            setInfoOverlayFeature(pointFeatures[0]);
        } else {
            setInfoOverlayFeature(null);
        }
    };


    const maxSpeedKmh = useMemo(() => (
        Math.max(0, ...getSmoothedSpeeds(pingSession?.pings ?? []).map((s) => s ?? 0))
    ), [pingSession]);

    const positionStyle = usePositionGradientStyle();
    const speedStyle = useSpeedGradientStyle(maxSpeedKmh);
    const featureStyle = showSpeedColors ? speedStyle : positionStyle;

    return (<>

        {loadingMessage && <LoadingSpinner Message={loadingMessage} />}
        {!loadingMessage && isLoaded && pingSessionId != undefined && <SessionSummary session={pingSession} showSpeedColors={showSpeedColors} onToggleSpeedColors={setShowSpeedColors} />}
        <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
            <PinPointNavbar />
            {showSessionsModal && <Container style={{
                maxWidth: "1200px",
                position: "fixed",
                top: "100px",
                left: "50%",
                transform: "translate(-50%, 0%)",
                zIndex: "10", // This ensures the container stays above the map
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "10px",
                boxShadow: "0 20px 16px 0 rgba(0,0,0,0.4)",
                minHeight: "600px",
                maxHeight: "80%",
                //backgroundColor: "#393040",
                //color: "white"
                overflowY: "auto"
            }}>
                <Sessions onView={handleSessionSelected} />
            </Container>}
            <div className="sessionViewMapContainer">
                <Map view={view} onClick={handlePointerClick}>
                    <Layers>
                        <TileLayer source={osm()} zIndex={0} />
                        <VectorLayer style={featureStyle} zIndex={2}>
                            <MyVectorSource features={features} />
                        </VectorLayer>
                    </Layers>
                    <Overlays>
                        <PinPointInfoOverlay
                            feature={infoOverlayFeature}
                            onNextFeatureClick={handleNextFeatureClick}
                        />
                    </Overlays>
                </Map>
            </div></div></>
    )
}